/**
 * Übergabe-Notiz (#158) — `intake_handover_fields.uebergabe_intake.sonstiges` in der Staffing-Maske
 * (Staffingseite, Projektdetail-Tab „Staffing"). Bewusst NICHT Teil von `<StaffingMaske>`: die
 * Übergabe zeigt die Maske auch, dort steht dasselbe Feld aber schon als „Sonstiges" im
 * Intake-Block daneben.
 *
 * Speichert beim Verlassen/Strg+Enter über `InlineFeld` (Escape verwirft, Fehler → Toast + alter
 * Wert) an die eigene Route `PATCH /v1/projects/:id/uebergabe-notiz`, die NUR `sonstiges` schreibt.
 * Recht `edit_intake` (`useCanEditIntake`); ohne Recht nur Text, bei langem Text scrollbar.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useCanEditIntake } from '../../lib/permissions.js';
import { InlineFeld } from './inline-feld.js';
import { maskeQueryKeys } from './staffing-maske/maske-kontext.js';

/** Zusätzlich zu `maskeQueryKeys`: Übergabe-Karten und KFML-Worklist zeigen das Feld auch. */
const WEITERE_KEYS: readonly (readonly string[])[] = [
  ['v2-handover-projects'],
  ['v2-kfml-worklist'],
];

export function UebergabeNotiz({
  project,
  sonstiges,
}: {
  project: Parameters<typeof useCanEditIntake>[0] & { project_id: string };
  sonstiges: string | null | undefined;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const kannBearbeiten = useCanEditIntake(project);
  const label = t('v2.uebergabe_notiz.label', 'Übergabe-Notiz');
  const wert = sonstiges?.trim() ? sonstiges : null;

  const speichern = async (neu: string | null) => {
    await api.setUebergabeNotiz(project.project_id, neu);
    await Promise.all(
      [...maskeQueryKeys(project.project_id), ...WEITERE_KEYS].map((k) =>
        qc.invalidateQueries({ queryKey: [...k] }),
      ),
    );
  };

  return (
    <div>
      <div className="text-xs mb-1" style={{ color: 'var(--ink-2)' }}>
        {label}
      </div>
      {kannBearbeiten ? (
        <InlineFeld<string>
          wert={wert}
          art="textfeld"
          kannBearbeiten
          label={label}
          platzhalter={t('v2.uebergabe_notiz.platzhalter', 'Notiz zur Übergabe …')}
          zeilen={{ leer: 2, gefuellt: 4 }}
          onSpeichern={speichern}
        />
      ) : (
        <div
          className="whitespace-pre-wrap text-sm max-h-24 overflow-y-auto"
          style={{ color: wert ? 'var(--ink)' : 'var(--ink-3)' }}
        >
          {wert ?? t('v2.uebergabe_notiz.leer', 'Keine Übergabe-Notiz')}
        </div>
      )}
    </div>
  );
}
